/**
 * Datumswerte aus Formularfeldern lesen, ohne die Fallen von `new Date(string)`.
 *
 * <p>Ein Datumsfeld (`bic-input` mit `bicDatepickerInput`) meldet je nach Weg einen `Date` (Auswahl
 * im Kalender), den Feldtext als deutschen String (`TT.MM.JJJJ`, getippt oder beim Schließen) oder
 * einen Wert vom Backend (ISO). `new Date('05.10.2026')` liest den deutschen Text aber als
 * Monat.Tag und liefert den 10.05.2026, `new Date('25.10.2026')` ein „Invalid Date“. Deshalb wird
 * hier jedes Format ausdrücklich erkannt.
 */

const GERMAN_DATE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T/;

/** Tag 0:00 Uhr <b>lokaler</b> Zeit; `null`, wenn es den Tag nicht gibt (z. B. 31.02.). */
function localDay(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : null;
}

/** `5.10.2026` / `05.10.2026` → 05.10.2026 um 0:00 Uhr lokaler Zeit, sonst `null`. */
export function parseGermanDate(text: string | null | undefined): Date | null {
  const match = GERMAN_DATE.exec((text ?? '').trim());
  return match ? localDay(Number(match[3]), Number(match[2]), Number(match[1])) : null;
}

/**
 * Datumswert eines Formularfelds als `Date`, ungültig oder leer als `null`.
 *
 * <ul>
 *   <li>`Date`: unverändert, ein „Invalid Date“ wird `null`.</li>
 *   <li>`d.M.yyyy` / `dd.MM.yyyy`: Tag 0:00 Uhr lokaler Zeit.</li>
 *   <li>`yyyy-MM-dd`: Tag 0:00 Uhr lokaler Zeit (nicht UTC wie bei `new Date`).</li>
 *   <li>ISO-Zeitpunkt (`yyyy-MM-ddTHH:mm…`, so liefert das Backend): wie `new Date`.</li>
 *   <li>alles andere: `null`.</li>
 * </ul>
 */
export function toLocalDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const text = value.trim();
  if (GERMAN_DATE.test(text)) {
    return parseGermanDate(text);
  }
  const isoDate = ISO_DATE.exec(text);
  if (isoDate) {
    return localDay(Number(isoDate[1]), Number(isoDate[2]), Number(isoDate[3]));
  }
  if (ISO_DATE_TIME.test(text)) {
    const date = new Date(text);
    return isNaN(date.getTime()) ? null : date;
  }
  return null;
}
